import { FileRepository } from "@amityco/ts-sdk";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import { AdminTag, HighlightText } from "@/components";
import { Pin } from "@/icons";
import { useAmityAuth, useTranslate } from "@/providers/context";
import { cn, formatRelativeDateForCommunity } from "@/utils";

import {
  CommentButton,
  CommunityPostDropdown,
  CommunityUserAvatar,
  FlagCount,
  LikeButton,
} from "../";
import { SEARCH_PARAM } from "../../const";
import { useCommunityFeed } from "../../providers/context/useCommunityFeed";
import { PostItem } from "../../types";
import { isCommunityAdmin } from "../../util";

export const CommunityPost = ({
  post,
  isDetailsScreen,
  isJoined,
  hideTopBorder,
  onBlockInteraction,
}: {
  post: PostItem;
  isDetailsScreen?: boolean;
  isJoined?: boolean;
  hideTopBorder?: boolean;
  onBlockInteraction?: () => void;
}) => {
  const { translate } = useTranslate();
  const navigate = useNavigate();
  const { registerPost, unregisterPost, setClickedPostId } = useCommunityFeed();
  const postRef = useRef<HTMLDivElement>(null);
  const { authState } = useAmityAuth();

  const [searchParams] = useSearchParams();
  const query = searchParams.get(SEARCH_PARAM);

  // Register post for visibility tracking
  useEffect(() => {
    if (postRef.current) {
      registerPost(post.id, postRef.current);
    }

    return () => {
      unregisterPost(post.id);
    };
  }, [post.id, registerPost, unregisterPost]);

  const isAdmin = isCommunityAdmin(authState?.authenticatedUser);

  const contentRef = useRef<HTMLDivElement>(null);
  const [isOverflowing, setIsOverflowing] = useState(false);

  const interactionIsDisabled = !isDetailsScreen && !isJoined;

  useEffect(() => {
    const checkOverflow = () => {
      if (contentRef.current) {
        const { scrollHeight, clientHeight } = contentRef.current;
        setIsOverflowing(scrollHeight > clientHeight);
      }
    };

    checkOverflow();
    window.addEventListener("resize", checkOverflow);
    return () => window.removeEventListener("resize", checkOverflow);
  }, [post.textContent]);

  const handleNavigateToPost = useCallback(() => {
    if (interactionIsDisabled) {
      onBlockInteraction?.();
      return;
    }

    setClickedPostId(post.id);

    navigate(`${window.location.pathname}/${post.id}`, {
      state: { fromQuery: query },
    });
  }, [
    post.id,
    navigate,
    query,
    interactionIsDisabled,
    onBlockInteraction,
    setClickedPostId,
  ]);

  const processedAttachments = useMemo(() => {
    if (!post.attachments || post.attachments.length === 0) {
      return [];
    }

    return post.attachments.map((attachment) => {
      const fullSizeUrl = FileRepository.fileUrlWithSize(
        attachment.fileUrl,
        "full",
      );
      const aspectRatio =
        attachment.attributes.metadata.width /
        attachment.attributes.metadata.height;

      return {
        ...attachment,
        fullSizeUrl,
        aspectRatio,
      };
    });
  }, [post.attachments]);

  return (
    <>
      {!hideTopBorder && (
        <div className="ml-3 h-[1px] w-[calc(100%-12px)] bg-border" />
      )}
      <div
        ref={postRef}
        className={cn(
          "flex flex-col gap-5 pb-8",
          hideTopBorder ? "pt-0" : "pt-8",
        )}
      >
        <div className="flex flex-col gap-5 pl-3">
          <div className="flex items-center gap-4">
            <CommunityUserAvatar user={post.postCreator} />
            <div className="w-full overflow-hidden">
              <div className="flex w-full items-center justify-between gap-2">
                <div className="flex items-center gap-2 overflow-hidden">
                  <div className="p1-semibold ellipsis">
                    {post.postCreator.displayName}
                  </div>
                  {isCommunityAdmin(post.postCreator) && <AdminTag />}
                </div>
                {!interactionIsDisabled && (
                  <CommunityPostDropdown
                    post={post}
                    className="flex items-center"
                    isDetailsScreen={isDetailsScreen}
                  />
                )}
              </div>
              <div className="flex items-center gap-2">
                <div className="p1 text-item-secondary">
                  {formatRelativeDateForCommunity(post.date)}
                </div>
                {isAdmin && post.flagCount > 0 && (
                  <FlagCount value={post.flagCount} />
                )}
                {post.pinned && (
                  <div className="p2-medium ml-1 flex items-center gap-1 text-brand-primary">
                    <Pin width={20} height={20} />
                    {translate("Pinned")}
                  </div>
                )}
              </div>
            </div>
          </div>
          <div
            className={cn(
              "flex flex-col gap-5",
              isDetailsScreen ? "cursor-default" : "cursor-pointer",
            )}
            onClick={isDetailsScreen ? undefined : handleNavigateToPost}
          >
            <div>
              <div
                ref={contentRef}
                className={cn(
                  "p2-regular whitespace-pre-wrap",
                  !isDetailsScreen && "line-clamp-3",
                )}
              >
                <HighlightText
                  text={post.textContent ?? ""}
                  highlight={query ?? undefined}
                />
              </div>
              {isOverflowing && (
                <div className="p2-bold w-fit cursor-pointer text-item-primary hover:text-item-secondary active:text-item-primary">
                  {translate("See More")}
                </div>
              )}
            </div>
            {processedAttachments.length > 0 && (
              <div className="flex flex-col gap-4">
                {processedAttachments.map((attachment) => {
                  if (attachment.type === "image") {
                    return (
                      <div
                        key={attachment.fileId}
                        className="relative w-full"
                        style={{
                          paddingBottom: `${(1 / attachment.aspectRatio) * 100}%`,
                        }}
                      >
                        <div className="absolute inset-0 animate-pulse rounded-xl bg-surface-tertiary" />
                        <img
                          src={attachment.fullSizeUrl}
                          alt={attachment.type}
                          className="absolute inset-0 h-full w-full rounded-xl object-cover"
                        />
                      </div>
                    );
                  }

                  if (attachment.type === "video") {
                    return (
                      <video
                        controls
                        onClick={(e) => e.stopPropagation()}
                        controlsList="nodownload"
                        key={attachment.fileId}
                        src={attachment.fullSizeUrl}
                        className="w-full rounded-xl"
                      >
                        {translate(
                          "Your browser does not support this video format.",
                        )}
                      </video>
                    );
                  }

                  return null;
                })}
              </div>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <LikeButton
            item={{
              id: post.id,
              type: "post",
              textContent: post.textContent ?? "",
              userId: post.postCreator.userId,
            }}
            haveLiked={post.haveLiked}
            likesCount={post.likes}
            type="post"
            overrideClick={
              interactionIsDisabled ? onBlockInteraction : undefined
            }
          />
          {!isDetailsScreen && (
            <CommentButton
              value={post.comments}
              onClick={handleNavigateToPost}
            />
          )}
        </div>
      </div>
    </>
  );
};
